import { useRef, useState, useMemo, useEffect, useCallback } from 'react';
import { AgGridReact } from 'ag-grid-react';
import 'ag-grid-community/styles/ag-grid.css';
import 'ag-grid-community/styles/ag-theme-alpine.css';
import 'ag-grid-community/styles/ag-theme-balham.css';
import { useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { getActiveOwners, getGridDataByModuleKey, getSalesOrder } from '../../redux/selector';
import apiServices from '../../services/service';
import { fetchDataAndUpdateStore } from '../../redux/actions';
import { salesOrderInputData } from '../../shared/constants';
import { toast } from 'react-toastify';
import useSalesColumnDefs from './useSalesColumnDefs';
import gridOptionsConfig from '../sharedChild/grid/gridOptionsConfig';
import useFetchIfNeeded from '../../redux/useFetchIfNeeded';
import { isLoadingByKey } from '../../shared/controls/methods/getLoadingState';
import Loader from '../../shared/component/Loader';
import FilterHandler from '../sharedChild/grid/filterHandler';

export const SalesGrid = () => {
  const stateData = useSelector((state) => state);
  let storeLoading = isLoadingByKey(stateData, 'salesOrder', '');
  const gridRef = useRef();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(storeLoading);
  const rowData = useSelector(getSalesOrder);
  const dispatch = useDispatch();
  const gridOptions = gridOptionsConfig('salesOrder', navigate);
  const gridData = useSelector(getGridDataByModuleKey('salesOrder'));
  const [gridParams, setGridParams] = useState(null);
  const owner = useSelector(getActiveOwners);

  useEffect(() => {
    setLoading(storeLoading);
  }, [storeLoading]);

  useFetchIfNeeded(getSalesOrder, fetchDataAndUpdateStore, {
    key: 'salesOrder',
    inputData: salesOrderInputData,
  });

  const handleClickOpenEditForm = (paramsValue) => {
    navigate('/sales/createSalesOrder', {
      state: paramsValue,
    });
  };

  const handleOpenViewForm = (paramsValue) => {
    navigate('/sales/viewSalesOrder', {
      state: paramsValue,
    });
  };

  const handleClickDelete = (paramsValue) => {
    setLoading(true);
    // Creating the desired JSON object
    const data = {
      id: [paramsValue.id],
    };
    apiServices
      .deleteSalesOrder(data)
      .then((response) => {
        if (response.status === 200) {
          dispatch(
            fetchDataAndUpdateStore({
              key: 'salesOrder',
              inputData: salesOrderInputData,
            })
          );
          setLoading(false);
          toast.success('Sales order deleted Successfully!', {
            position: 'top-right',
          });
        }
      })
      .catch((error) => {
        console.error('Error while deleting lead:', error);
        setLoading(false);
      });
  };

  const columnDefs = useSalesColumnDefs({
    handleClickDelete,
    handleOpenViewForm,
    handleClickOpenEditForm,
    owner
  });

  const sideBar = useMemo(() => {
    return {
      toolPanels: [
        {
          id: 'columns',
          labelDefault: 'Columns',
          labelKey: 'columns',
          iconKey: 'columns',
          toolPanel: 'agColumnsToolPanel',
          // minWidth: 130,
          // width: 130,
          // maxWidth: 130,
          toolPanelParams: {
            suppressPivotMode: true, // 👈 Hide Pivot Mode toggle
            suppressRowGroups: false, // ✅ Allow row grouping
            suppressValues: false, // ✅ Allow aggregation if needed
            suppressPivots: true, // 👈 Hide the pivot section for columns
          },
        },
        {
          id: 'filters',
          labelDefault: 'Filters',
          labelKey: 'filters',
          iconKey: 'filter',
          toolPanel: 'agFiltersToolPanel',
          // minWidth: 180,
          // maxWidth: 400,
          // width: 250,
        },
      ].filter(Boolean),
      position: 'left',
      defaultToolPanel: 'none', //show off filter tab bydefault.
    };
  }, []);
  const handleGridReady = (params) => {
    gridRef.current = params;
    setGridParams(params);
    const columnOrderFromStore = gridData?.columnState;

    if (columnOrderFromStore) {
      params.columnApi.applyColumnState({
        state: columnOrderFromStore,
        applyOrder: true,
      });
    } else {
      // apply default column visibility if no saved state
      const fieldsToHide = [
        // 'tagIds',
        // 'campaignId',
        // 'createdDateTime',
        // 'updatedDateTime',
        // 'companyLinkedIn',
        // 'website',
        // 'annualRevenue',
        // 'writtenPremium',
        // 'noOfEmployees',
        // 'noOfBeds',
        // 'sourceName',
        // 'otherSourceName',
        // 'salutation',
        // 'firstName',
        // 'middleName',
        // 'lastName',
        // 'revenueType',
        // 'title',
        // 'secondaryEmail',
        // 'mobile',
        // 'directPhone',
        // 'alternatePhone',
        // 'street',
        // 'city',
        // 'emailOptOut',
        // 'state',
        // 'zipCode',
        // 'country',
        // 'description',
        // 'technologyDetails',
      ];

      const defaultState = params.columnApi.getColumnState().map((col) => ({
        ...col,
        hide: fieldsToHide.includes(col.colId),
      }));

      params.columnApi.applyColumnState({
        state: defaultState,
        applyOrder: true,
      });
    }
  };

  useEffect(() => {
    if (
      gridRef.current?.api &&
      gridData?.salesOrder?.length &&
      gridData?.inputPayload?.filterJson
    ) {
      try {
        // Parse filterJson
        const filterJson = JSON.parse(gridData.inputPayload.filterJson);

        // Extract campaign IDs
        const campaignIds = filterJson?.campaignId?.conditions
          ? filterJson.campaignId.conditions.map((condition) => condition.filter)
          : filterJson?.campaignId?.filter
            ? [filterJson.campaignId.filter]
            : [];

        // Extract tag IDs
        const tagIds = filterJson?.tagIds?.conditions
          ? filterJson.tagIds.conditions.map((condition) => condition.filter)
          : filterJson?.tagIds?.filter
            ? [filterJson.tagIds.filter]
            : [];

        // Create a mapping of campaignId -> campaignName
        const campaignMapping = rowData.reduce((acc, campaign) => {
          acc[campaign.id] = campaign.campaignName;
          return acc;
        }, {});

        // Get campaign names from matched IDs
        const matchedCampaignNames = campaignIds.map(
          (id) => campaignMapping[id] || 'Unknown Campaign'
        );

        // Update contacts with campaignName and tags
        const updatedContacts = gridData.account.map((account) => ({
          ...account,
          campaignName: matchedCampaignNames, // Correct format
          // tags: matchedTagNames, // Flattened array
        }));

        // Update grid data
        const updatedGridData = {
          ...gridData,
          account: updatedContacts,
        };

        // Update the grid with new data
        gridRef.current.api.setGridOption('rowData', updatedGridData.account);
        gridRef.current.api.refreshCells();
      } catch (error) {
        console.error('Error parsing filterJson:', error);
      }
    }
  }, [gridData, rowData]);

  const filterDefaultSetRef = useRef(null);

  const handleFilterChanged = useCallback(
    (event) => {
      if (filterDefaultSetRef.current) {
        filterDefaultSetRef.current = false;
      } else {
        let filterModel = event.api.getFilterModel();

        // Create mappings for campaign names and tag names to their IDs
        const saleMapping = {};
        if (rowData?.length) {
          rowData.forEach((item) => {
            saleMapping[item.accountName] = item.id;
          });
        }

        const updatedFilterModel = Object.keys(filterModel).reduce((acc, dynamicFilterKey) => {
          let filterData = filterModel[dynamicFilterKey];

          if (dynamicFilterKey === 'campaignId') {
            // Convert campaign names to campaign IDs
            const idValues = filterData.values
              .map((name) => saleMapping[name])
              .filter((id) => id !== undefined);

            if (idValues.length > 0) {
              acc[dynamicFilterKey] = {
                filterType: 'text',
                operator: 'OR',
                conditions: idValues.map((id) => ({
                  filterType: 'text',
                  type: 'contains',
                  filter: id, // Ensure we send ID, not name
                })),
              };
            }
          }

          // Keep Other Filters Unchanged
          else {
            acc[dynamicFilterKey] = filterData;
          }

          return acc;
        }, {});

        const payload = {
          ...gridData?.inputPayload,
          filterJson: JSON.stringify(updatedFilterModel),
          pageNumber: 1, // Reset to first page on filter change
        };

        dispatch(
          fetchDataAndUpdateStore({
            key: 'salesOrder',
            inputData: payload,
          })
        );
      }
    },
    [dispatch, gridData?.inputPayload, rowData]
  );

  return (
    <div style={{ width: '100%', overflowX: 'auto' }}>
      {loading && <Loader />}
      <div
        className="ag-theme-balham tbl_height"
        style={{
          // minWidth: "1333px",
          height: 'calc(100vh - 138px)',
        }}
      >
        <AgGridReact
          ref={gridRef}
          rowData={rowData}
          columnDefs={columnDefs}
          defaultColDef={{
            sortable: true,
            filter: true,
            resizable: true,
            minWidth: 130, // Set minWidth for all columns instead of flex
          }}
          domLayout="normal"
          sideBar={sideBar}
          gridOptions={gridOptions}
          onFilterChanged={handleFilterChanged}
          onGridReady={handleGridReady}
        />
        {gridParams && (
          <FilterHandler
            params={gridParams}
            gridData={gridData}
            columnDefs={columnDefs}
            filterDefaultSetRef={filterDefaultSetRef}
          />
        )}
      </div>
    </div>
  );
};

export default SalesGrid;
